class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
      cartItems.updateQuantity(this.dataset.index, 0, event);
    });
  }
}

customElements.define('cart-remove-button', CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement =
      document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener('change', debouncedOnChange.bind(this));
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, async (event) => {
      if (event.source === 'cart-items') {
        return;
      }
      return await this.onCartUpdate();
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}, #Drawer-quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;
    let message = '';

    if (inputValue < event.target.dataset.min) {
      message = window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min);
    } else if (inputValue > parseInt(event.target.max)) {
      message = window.quickOrderListStrings.max_error.replace('[max]', event.target.max);
    } else if (inputValue % parseInt(event.target.step) !== 0) {
      message = window.quickOrderListStrings.step_error.replace('[step]', event.target.step);
    }

    if (message) {
      this.setValidity(event, index, message);
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      this.updateQuantity(
        index,
        inputValue,
        event,
        document.activeElement.getAttribute('name'),
        event.target.dataset.quantityVariantId
      );
    }
  }

  onChange(event) {
    this.validateQuantity(event);
  }

  get isCartDrawer() {
    if (!this._isCartDrawer) {
      this._isCartDrawer = this.tagName === 'CART-DRAWER-ITEMS';
    }

    return this._isCartDrawer;
  }

  async onCartUpdate() {
    const targetSectionId = this.isCartDrawer ? 'cart-drawer' : 'main-cart-items';
    const selectors = this.isCartDrawer ? ['cart-drawer-items', '.cart-drawer__footer'] : ['cart-items', '.cart__footer'];

    try {
      const response = await fetch(`${routes.cart_url}?section_id=${targetSectionId}`);
      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      const targetCartItems = html.querySelector('cart-items, cart-drawer-items');
      if (targetCartItems) {
        this.handleFreeProduct(targetCartItems);
      } else {
        this.handleFreeProduct();
      }

      for (const selector of selectors) {
        const targetElement = document.querySelector(selector);
        const sourceElement = html.querySelector(selector);
        if (targetElement && sourceElement) {
          targetElement.replaceWith(sourceElement);
        }
      }
    } catch (error) {
      console.error(error);
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items').dataset.id,
        selector: '.js-contents',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer').dataset.id,
        selector: '.js-contents',
      },
    ];
  }

  updateQuantity(line, quantity, event, name, variantId) {
    this.enableLoading(line);

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });
    const eventTarget = event.currentTarget instanceof CartRemoveButton ? 'clear' : 'change';

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);

        CartPerformance.measure(`${eventTarget}:paint-updated-sections"`, () => {
          const quantityElement =
            document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);
          const items = document.querySelectorAll('.cart-item');

          if (parsedState.errors) {
            quantityElement.value = quantityElement.getAttribute('value');
            this.updateLiveRegions(line, parsedState.errors);
            return;
          }

          this.classList.toggle('is-empty', parsedState.item_count === 0);
          const cartDrawerWrapper = document.querySelector('cart-drawer');
          const cartFooter = document.getElementById('main-cart-footer');

          if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
          if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

          this.getSectionsToRender().forEach((section) => {
            const elementToReplace =
              document.getElementById(section.id).querySelector(section.selector) ||
              document.getElementById(section.id);
            elementToReplace.innerHTML = this.getSectionInnerHTML(
              parsedState.sections[section.section],
              section.selector
            );
          });
          const updatedValue = parsedState.items[line - 1] ? parsedState.items[line - 1].quantity : undefined;
          let message = '';
          if (items.length === parsedState.items.length && updatedValue !== parseInt(quantityElement.value)) {
            if (typeof updatedValue === 'undefined') {
              message = window.cartStrings.error;
            } else {
              message = window.cartStrings.quantityError.replace('[quantity]', updatedValue);
            }
          }
          this.updateLiveRegions(line, message);

          const lineItem =
            document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);
          if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
            cartDrawerWrapper
              ? trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`))
              : lineItem.querySelector(`[name="${name}"]`).focus();
          } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
            trapFocus(cartDrawerWrapper.querySelector('.drawer__inner-empty'), cartDrawerWrapper.querySelector('a'));
          } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
            trapFocus(cartDrawerWrapper, document.querySelector('.cart-item__name'));
          }
        });

        CartPerformance.measureFromEvent(`${eventTarget}:user-action`, event);
        
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState, variantId: variantId });
      })
      .catch(() => {
        this.querySelectorAll('.loading__spinner').forEach((overlay) => overlay.classList.add('hidden'));
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        this.disableLoading(line);
      });
  }

  // updateContents(parsedState) {
  //   this.classList.toggle('is-empty', parsedState.item_count === 0);
  //   const cartDrawerWrapper = document.querySelector('cart-drawer');
  //   const cartFooter = document.getElementById('main-cart-footer');

  //   if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
  //   if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

  //   this.getSectionsToRender().forEach((section) => {
  //     const elementToReplace =
  //       document.getElementById(section.id).querySelector(section.selector) ||
  //       document.getElementById(section.id);
  //     elementToReplace.innerHTML = this.getSectionInnerHTML(
  //       parsedState.sections[section.section],
  //       section.selector
  //     );
  //   });

  //   publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState });
  // }

  updateLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError) {
      lineItemError.querySelector('.cart-item__error-text').textContent = message;
      setTimeout(() => {
        lineItemError.querySelector('.cart-item__error-text').textContent = '';
      },5000);
    }

    this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus =
      document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
    cartStatus.setAttribute('aria-hidden', false);

    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }

  // TODO: Update cart bubble
  handleFreeProduct(targetCartItems = this) {
    if (!targetCartItems) {
      console.error('Free Product Error: No target cart items found');
      return;
    }

    const freeGiftEnabled = targetCartItems.getAttribute('free-gift-enabled') === 'true';
    const freeGiftLineItemKey = targetCartItems.getAttribute('free-gift-line-item-key');

    const freeGiftThreshold = parseInt(targetCartItems.getAttribute('free-gift-threshold'), 10) / 100;
    const cartTotal = parseInt(targetCartItems.getAttribute('cart-total'), 10) / 100;
    const hasFreeGift = !!(targetCartItems.hasAttribute('has-free-gift') || targetCartItems.querySelector('[data-free-product-id]'));
    const freeGiftVariantId = targetCartItems.getAttribute('free-gift-variant-id');

    const freeGiftEligible = cartTotal >= freeGiftThreshold;

    if (freeGiftEnabled && freeGiftEligible && !hasFreeGift) {
      window.freeProductAdd(freeGiftVariantId);
    } else if (freeGiftLineItemKey && hasFreeGift && (!freeGiftEnabled || !freeGiftEligible)) {
      window.freeProductRemove(freeGiftLineItemKey);
    }
  }

  getSectionInnerHTML(html, selector) {
    const parsedHtml = new DOMParser().parseFromString(html, 'text/html');

    const cartItems = parsedHtml.querySelector('cart-items, cart-drawer-items');
    if (cartItems) {
      this.handleFreeProduct(cartItems);
    }

    return parsedHtml.querySelector(selector).innerHTML;
  }

  enableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.add('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) => overlay.classList.remove('hidden'));

    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  disableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.remove('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    cartItemElements.forEach((overlay) => overlay.classList.add('hidden'));
    cartDrawerItemElements.forEach((overlay) => overlay.classList.add('hidden'));
  }
}

customElements.define('cart-items', CartItems);

document.addEventListener('DOMContentLoaded', () => {
  const cartItems = document.querySelector('cart-items, cart-drawer-items');
  if (cartItems) {
    cartItems.handleFreeProduct();
  }
});

if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();
      }

      /* bs-add
        - insure element is connected before attempting to access textarea
      */
      connectedCallback() {
        this.textarea = this.querySelector('textarea');

        /* bs-add
          - get maxLength from textarea attribute
        */
        const maxLengthAttr = this.textarea.getAttribute('maxLength');
        this.maxLength = parseInt(maxLengthAttr, 10) || 0;

        /* bs-add
          - validate maxLength is a proper number and positive
        */
        if (this.maxLength > 0) {
          this.counter = document.createElement('span');
          this.counter.classList.add('field__counter', 'caption');
          this.counter.textContent = `${this.textarea.value.length || 0}/${this.maxLength}`;
          this.appendChild(this.counter);
        }

        this.textarea.addEventListener(
          'input',
          debounce((event) => {
            /* bs-add
              - update counter indicator
            */
            if (this.counter && this.maxLength > 0) {
              this.counter.textContent = `${event.target.value.length}/${this.maxLength}`;
            }

            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } }).then(() =>
              CartPerformance.measureFromEvent('note-update:user-action', event)
            );
          }, ON_CHANGE_DEBOUNCE_TIMER)
        );
      }
    }
  );
}
